import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import { InfoBanner } from '@/common-components/elements/InfoBanner';
import routes from '@/routes/routes.constants';

import { selectedPlanAtom, selectedPayerIdAtom } from '../atoms';

import { PatientBenefitsLayout } from './PatientBenefitsLayout';
import { SectionCardWithHeader } from './sectionPages/common/SectionCardWithHeader';
import { CoverageDetailsSection } from './sectionPages/CoverageDetailsSection';
import { CoverageSummarySection } from './sectionPages/CoverageSummarySection';
import { LimitationsAndDisclaimersSection } from './sectionPages/limitationsAndDisclaimers/LimitationsAndDisclaimersSection';
import { MaximumsAndDeductiblesSection } from './sectionPages/maximumsAndDeductibles/MaximumsAndDeductiblesSection';
import { PatientInformationSection } from './sectionPages/PatientInformationSection';
import { PlanSummarySection } from './sectionPages/PlanSummarySection';
import { TreatmentHistorySection } from './sectionPages/treatmentHistory/TreatmentHistorySection';
import { WaitingPeriodsSection } from './sectionPages/waitingPeriods/WaitingPeriodsSection';

export const PatientBenefitsCombinedPage = () => {
  const currentPlan = useAtomValue(selectedPlanAtom);

  const { t } = useLingui();
  const history = useHistory();
  const setSelectedPayerId = useSetAtom(selectedPayerIdAtom);

  // Check if the currently selected plan has a plan type
  const hasPlanType = currentPlan?.plan?.planType != null;

  const handleSetUpCredentials = () => {
    // Set the payer ID from the selected plan and navigate to connect-payers page
    const payerId = currentPlan?.payerId;
    if (payerId) {
      setSelectedPayerId(payerId);
      history.push(routes.CONNECT_PAYERS);
    }
  };

  return (
    <PatientBenefitsLayout>
      <div className="vai-space-y-8">
        {/* Incomplete Benefits Information Banner */}
        <InfoBanner
          title={t`Enable More Benefits Information`}
          description={t`Benefits information will be limited until you connect Videa to this payer portal. We'll help you get set up.`}
          variant="primary"
          actionButton={
            <Button
              size="sm"
              className="vai-bg-blue-600 hover:vai-bg-blue-700"
              onClick={handleSetUpCredentials}
            >
              <Trans>Set Up Credentials</Trans>
            </Button>
          }
          onClose={() => {
            // TODO: Handle close
          }}
        />

        {/* Plan Summary */}
        <PlanSummarySection />

        {/* Patient Information Section */}
        <div id="patient-information-section">
          <SectionCardWithHeader title={t`Patient Information`}>
            <PatientInformationSection />
          </SectionCardWithHeader>
        </div>

        {/* Coverage Summary Section - only show if selected plan has a plan type */}
        {hasPlanType && (
          <div id="coverage-summary-section">
            <SectionCardWithHeader title={t`Coverage Summary`}>
              <CoverageSummarySection />
            </SectionCardWithHeader>
          </div>
        )}

        {/* Coverage Details Section - only show if selected plan has a plan type */}
        {hasPlanType && (
          <div id="coverage-details">
            <SectionCardWithHeader title={t`Coverage Details`}>
              <CoverageDetailsSection />
            </SectionCardWithHeader>
          </div>
        )}

        {/* Maximums & Deductibles Section */}
        <div id="maximums-deductibles-section">
          <SectionCardWithHeader title={t`Maximums & Deductibles`}>
            <MaximumsAndDeductiblesSection />
          </SectionCardWithHeader>
        </div>

        {/* Limitations & Disclaimers Section */}
        <div id="limitations-and-disclaimers-section">
          <SectionCardWithHeader title={t`Limitations & Disclaimers`}>
            <LimitationsAndDisclaimersSection />
          </SectionCardWithHeader>
        </div>

        {/* Waiting Periods Section */}
        <div id="waiting-periods-section">
          <SectionCardWithHeader title={t`Waiting Periods`}>
            <WaitingPeriodsSection />
          </SectionCardWithHeader>
        </div>

        {/* Treatment History Section */}
        <div id="treatment-history-section">
          <SectionCardWithHeader title={t`Treatment History`}>
            <TreatmentHistorySection />
          </SectionCardWithHeader>
        </div>
      </div>
    </PatientBenefitsLayout>
  );
};
